import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchLogs } from "../../services/externalLogs";

/* ===================== TYPES ===================== */

export interface ExternalApiLog {
  _id: string;
  apiProvider: string;
  platform: string;
  method: string;
  apiName: string;
  statusCode: number;
  createdAt: string;
  serialNumber?: number;
  user?: string;
  responseBody?: { message?: string };
  filters?: {
    apiProvider?: string;
    platform?: string;
    id?: string;
    from?: string; // YYYY-MM-DD
    to?: string;   // YYYY-MM-DD
  };
}

export interface LogFilters {
  apiProvider?: string;
  platform?: string;
  from?: string;
  to?: string;
}

interface ExternalLogsState {
  logs: ExternalApiLog[];
  loading: boolean;
  total: number;

  // table state
  page: number;
  pageSize: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  searchQuery: string;
  filters: LogFilters;

  hasFetched: boolean;
  error?: string;
}

/* ===================== INITIAL STATE ===================== */

const initialState: ExternalLogsState = {
  logs: [],
  loading: false,
  total: 0,

  page: 1,
  pageSize: 10,
  sortBy: "createdAt",
  sortOrder: "desc",
  searchQuery: "",
  filters: {},

  hasFetched: false,
};

/* ===================== THUNK ===================== */
/**
 * Reads pagination, sorting, search & filters from Redux state
 * Token is the only argument
 */
export const fetchExternalLogsThunk = createAsyncThunk(
  "externalLogs/fetch",
  async (token: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { externalLogs: ExternalLogsState };
      const {
        page,
        pageSize,
        sortBy,
        sortOrder,
        searchQuery,
        filters,
      } = state.externalLogs;

      const response = await fetchLogs({
        page,
        pageSize,
        sortField: sortBy,
        sortOrder,
        search: searchQuery,
        filters,
        token,
      });
      console.log("FETCH PAGE:", page);

      return response;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

/* ===================== SLICE ===================== */

const externalLogsSlice = createSlice({
  name: "externalLogs",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<LogFilters>) => {
      state.filters = action.payload;
      state.page = 1;
    },
    resetFilters: (state) => {
      state.filters = {};
      state.searchQuery = "";
      state.page = 1;
    },
    forceRefreshLogs: (state) => {
      state.hasFetched = false;
    },
    setHasFetched: (state, action: PayloadAction<boolean>) => {
      state.hasFetched = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExternalLogsThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchExternalLogsThunk.fulfilled, (state, action: PayloadAction<any>) => {
        const { logs, totalCount } = action.payload;

        state.logs = logs.map((log: any, index: number) => ({
          _id: log._id,
          apiProvider: log.apiProvider,
          platform: log.platform,
          method: log.method,
          apiName: log.apiName,
          statusCode: Number(log.statusCode),
          createdAt: log.createdAt,
          serialNumber: (state.page - 1) * state.pageSize + index + 1,
          user: log.user,
          responseBody: log.responseBody,
        }));

        state.total = totalCount;
        state.loading = false;
        state.hasFetched = true;
      })
      .addCase(fetchExternalLogsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

/* ===================== EXPORTS ===================== */

export const {
  setPage,
  setPageSize,
  setSortBy,
  setSortOrder,
  setSearchQuery,
  setFilters,
  resetFilters,
  forceRefreshLogs,
  setHasFetched
} = externalLogsSlice.actions;

export default externalLogsSlice.reducer;
